// src/AdminEvents.js
import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { useOrganization } from './contexts/OrganizationContext';
import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  writeBatch, 
  getDoc 
} from "firebase/firestore";
import Header from "./components/ui/Header";
import { Link } from "react-router-dom";

function AdminEvents() {
  const { currentOrganization } = useOrganization();
  
  // フォーム状態
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    title: "",
    time: "",
    location: "",
    notes: "",
    items: "",
    notifyDate: "",
    reflectToShift: false, // ✅ 新規追加
  reflectMethod: "block" // ✅ 新規追加: "block" or "replace"
  });
  
  // 割り当てタイプ
  const [assignmentType, setAssignmentType] = useState("individual"); // "individual" または "group"
  const [selectedGroup, setSelectedGroup] = useState("");
  
  // UI状態
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [skillGroups, setSkillGroups] = useState([]);
  const [activeSection, setActiveSection] = useState("basic");
  const [showPreview, setShowPreview] = useState(false);
  const [previewMembers, setPreviewMembers] = useState([]);

  // スタッフとスキルグループのデータを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 組織に応じてスタッフデータを取得
        const staffQuery = currentOrganization?.id 
          ? collection(db, `organizations/${currentOrganization.id}/staffList`)
          : collection(db, "staffList");
        
        // スタッフデータとスキルグループを並列で取得
        const [staffSnapshot, groupsSnapshot] = await Promise.all([
          getDocs(staffQuery),
          getDocs(collection(db, "skillGroups"))
        ]);
        
        const staffData = staffSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const groupsData = groupsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setStaffList(staffData);
        setSkillGroups(groupsData);
      } catch (error) {
        console.error("データの取得に失敗:", error);
      }
    };
    
    fetchData();
  }, [currentOrganization]);

  // スキルグループのメンバープレビューを表示する
  const handleGroupPreview = async (groupId) => {
    if (!groupId) {
      setPreviewMembers([]);
      setShowPreview(false);
      return;
    }
    
    setShowPreview(true);
    
    try {
      // 選択されたグループを見つける
      const group = skillGroups.find(g => g.id === groupId);
      
      if (!group || !group.members || group.members.length === 0) {
        setPreviewMembers([]);
        return;
      }
      
      // メンバーIDに対応するスタッフデータを取得（旧データの staff.id (UUID) と新データの employee_id 両対応）
      const memberDetails = staffList
        .filter(staff => group.members.includes(staff.employeeId) || group.members.includes(staff.id))
        .map(staff => ({
          id: staff.employeeId,
          name: `${staff.lastName} ${staff.firstName}`
        }));
      
      setPreviewMembers(memberDetails);
    } catch (error) {
      console.error("メンバー詳細の取得エラー:", error);
    }
  };

  // 入力の変更を処理
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));  
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // スキルグループが変更された場合、プレビューを更新
    if (name === "skillGroup") {
      handleGroupPreview(value);
    }
  };

  // 割り当てタイプの変更処理
  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    
    // フォームデータをリセット
    if (type === "individual") {
      setSelectedGroup("");
      setShowPreview(false);
    } else {
      setFormData(prev => ({
        ...prev,
        name: ""
      }));
    }
    
    // 関連するエラーをリセット
    setErrors(prev => ({
      ...prev,
      name: null,
      skillGroup: null
    }));
  };

  // フォームの検証
  const validateForm = () => {
    const newErrors = {};
    
    // 共通の検証
    if (!formData.date) newErrors.date = "日付を入力してください";
    if (!formData.title) newErrors.title = "イベント名を入力してください";
    
    // 割り当てタイプに応じた検証
    if (assignmentType === "individual") {
      if (!formData.name) newErrors.name = "スタッフを選択してください";
    } else {
      if (!selectedGroup) newErrors.skillGroup = "スキルグループを選択してください";
    }
    
    // 日付が過去でないことを確認
    if (formData.date) {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.date = "過去の日付は選択できません";
      }
    }
    
    // 通知日がイベント日より前であることを確認
    if (formData.date && formData.notifyDate) {
      const eventDate = new Date(formData.date);
      const notifyDate = new Date(formData.notifyDate);
      
      if (notifyDate > eventDate) {
        newErrors.notifyDate = "通知日はイベント日より前に設定してください";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // イベントをスタッフのカレンダーに追加
  const addToStaffCalendar = async (staffId, eventData) => {
    try {
      // カレンダーコレクションへの参照を取得
      const calendarRef = doc(collection(db, "staffCalendars"), staffId);
      
      // 既存のカレンダーデータを取得
      const calendarSnap = await getDoc(calendarRef);
      
      let existingEvents = [];
      if (calendarSnap.exists()) {
        existingEvents = calendarSnap.data().events || [];
      }
      
      // 新しいイベントを追加
      const updatedEvents = [
        ...existingEvents,
        {
          ...eventData,
          type: "event", // イベントタイプを指定
          createdAt: new Date().toISOString()
        }
      ];
      
      // カレンダーを更新
      await setDoc(calendarRef, { 
        staffId, 
        events: updatedEvents 
      }, { merge: true });
      
    } catch (error) {
      console.error(`カレンダー更新エラー (${staffId}):`, error);
      throw error;
    }
  };

  // フォーム送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // イベントデータを作成
      const eventData = {
        date: formData.date,
        title: formData.title,
        time: formData.time || "",
        location: formData.location || "",
        notes: formData.notes || "",
        items: formData.items || "",
        notifyDate: formData.notifyDate || "",
        createdAt: new Date().toISOString()
      };
      
      // バッチ処理を初期化
      const batch = writeBatch(db);
      
      if (assignmentType === "individual") {
        // 個別スタッフへの割り当て
        const docRef = doc(collection(db, "staffEvents"), formData.name);
        
        // 既存のデータを取得
        const docSnap = await getDoc(docRef);
        let existingEvents = [];
        
        if (docSnap.exists()) {
          existingEvents = docSnap.data().events || [];
        }
        
        // イベントを追加
        batch.set(docRef, {
          staffId: formData.name,
          events: [...existingEvents, eventData]
        }, { merge: true });
        
        // カレンダーにも追加
        await addToStaffCalendar(formData.name, eventData);
        
      } else {
        // グループメンバー全員への割り当て
        const group = skillGroups.find(g => g.id === selectedGroup);
        
        if (group && group.members && group.members.length > 0) {
          // 各メンバーに対して処理
          const memberPromises = group.members.map(async (memberId) => {
            const docRef = doc(collection(db, "staffEvents"), memberId);
            
            // 既存のデータを取得
            const docSnap = await getDoc(docRef);
            let existingEvents = [];
            
            if (docSnap.exists()) {
              existingEvents = docSnap.data().events || [];
            }
            
            // イベントを追加
            batch.set(docRef, {
              staffId: memberId,
              events: [...existingEvents, eventData]
            }, { merge: true });
            
            // カレンダーにも追加
            await addToStaffCalendar(memberId, eventData);
          });
          
          // すべてのメンバー処理を待機
          await Promise.all(memberPromises);
        }
      }
      
      // バッチを実行
      await batch.commit();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // フォームをリセット
      setFormData({
        name: "",
        date: "",
        title: "",
        time: "",
        location: "",
        notes: "",
        items: "",
        notifyDate: ""
      });
      setSelectedGroup("");
      setShowPreview(false);
      
    } catch (error) {
      console.error("登録失敗:", error);
      setErrors({ submit: "登録に失敗しました: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  // 今日の日付をYYYY-MM-DD形式で取得
  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div style={styles.container}>
      <Header />
      
      <div style={styles.content}>
        <div style={styles.titleSection}>
          <h1 style={styles.mainTitle}>イベント登録</h1>
          <p style={styles.subtitle}>
            スタッフの研修、イベント、休暇などのスケジュールを登録します
          </p>
          <Link to="/admin-events-list" style={styles.listLink}>
            登録済みイベント一覧へ
          </Link>
        </div>
        
        <div style={styles.card}>
          <div style={styles.formNav}>
            <button 
              style={{
                ...styles.navButton, 
                ...(activeSection === "basic" ? styles.activeNavButton : {})
              }}
              onClick={() => setActiveSection("basic")}
            >
              基本情報
            </button>
            <button 
              style={{
                ...styles.navButton, 
                ...(activeSection === "details" ? styles.activeNavButton : {})
              }}
              onClick={() => setActiveSection("details")}
            >
              詳細情報
            </button>
          </div>
          
          <form onSubmit={handleSubmit} style={styles.form}>
            {activeSection === "basic" ? (
              <div style={styles.formSection}>
                {/* 割り当てタイプの選択 */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>割り当て方法</label>
                  <div style={styles.toggleContainer}>
                    <button
                      type="button"
                      onClick={() => handleAssignmentTypeChange("individual")}
                      style={{
                        ...styles.toggleButton,
                        ...(assignmentType === "individual" ? styles.activeToggle : {})
                      }}
                    >
                      個別スタッフ
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAssignmentTypeChange("group")}
                      style={{
                        ...styles.toggleButton,
                        ...(assignmentType === "group" ? styles.activeToggle : {})
                      }}
                    >
                      スキルグループ全員
                    </button>
                  </div>
                  <label style={styles.label}>
                  <input
                    type="checkbox"
                    name="reflectToShift"
                    checked={formData.reflectToShift}
                    onChange={handleChange}
                    style={{ marginRight: "0.5rem" }}
                  />
                  このイベントをシフト表に反映する
                </label>
                </div>

                {formData.reflectToShift && (
  <div style={styles.formGroup}>
    <label style={styles.label}>シフト表への反映方法</label>
    <select
      name="reflectMethod"
      value={formData.reflectMethod}
      onChange={handleChange}
      style={styles.input}
    >
      <option value="block">他のシフトをブロック（割り当て禁止）</option>
      <option value="replace">このイベント名で枠を埋める</option>
    </select>
  </div>
)}
                
                {/* 割り当て先の選択（条件分岐） */}
                {assignmentType === "individual" ? (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      スタッフ <span style={styles.required}>*</span>
                    </label>
                    <select
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      style={{
                        ...styles.input,
                        ...(errors.name ? styles.inputError : {})
                      }}
                    >
                      <option value="">スタッフを選択</option>
                      {staffList.map(staff => (
                        <option key={staff.id} value={staff.employeeId}>
                          {staff.lastName} {staff.firstName}
                        </option>
                      ))}
                    </select>
                    {errors.name && <div style={styles.errorText}>{errors.name}</div>}
                  </div>
                ) : (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      スキルグループ <span style={styles.required}>*</span>
                    </label>
                    <select
                      name="skillGroup"
                      value={selectedGroup}
                      onChange={(e) => {
                        setSelectedGroup(e.target.value);
                        handleGroupPreview(e.target.value);
                      }}
                      style={{
                        ...styles.input,
                        ...(errors.skillGroup ? styles.inputError : {})
                      }}
                    >
                      <option value="">グループを選択</option>
                      {skillGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.members?.length || 0}名)
                        </option>
                      ))}
                    </select>
                    {errors.skillGroup && <div style={styles.errorText}>{errors.skillGroup}</div>}
                    
                    {/* グループメンバープレビュー */}
                    {showPreview && previewMembers.length > 0 && (
                      <div style={styles.previewBox}>
                        <h4 style={styles.previewTitle}>所属メンバー ({previewMembers.length}名)</h4>
                        <div style={styles.previewMembers}>
                          {previewMembers.map(member => (
                            <span key={member.id} style={styles.memberChip}>
                              {member.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {showPreview && previewMembers.length === 0 && (
                      <div style={styles.emptyPreview}>
                        このグループにはメンバーがいません
                      </div>
                    )}
                  </div>
                )}
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    日付 <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    min={getTodayString()}
                    style={{
                      ...styles.input,
                      ...(errors.date ? styles.inputError : {})
                    }}
                  />
                  {errors.date && <div style={styles.errorText}>{errors.date}</div>}
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    イベント名 <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    placeholder="例: 心臓血管外科研修"
                    value={formData.title}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.title ? styles.inputError : {})
                    }}
                  />
                  {errors.title && <div style={styles.errorText}>{errors.title}</div>}
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>時間</label>
                  <input
                    type="text"
                    name="time"
                    placeholder="例: 13:00〜17:00"
                    value={formData.time}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>場所</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="例: 研修センター3階"
                    value={formData.location}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => setActiveSection("details")}
                    style={styles.nextButton}
                  >
                    次へ：詳細情報
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.formSection}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>事前課題・メモ</label>
                  <textarea
                    name="notes"
                    placeholder="例: 血管縫合の基礎を予習しておくこと"
                    value={formData.notes}
                    onChange={handleChange}
                    style={{...styles.input, ...styles.textarea}}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>持ち物</label>
                  <input
                    type="text"
                    name="items"
                    placeholder="例: 筆記用具、白衣"
                    value={formData.items}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>通知予定日</label>
                  <input
                    type="date"
                    name="notifyDate"
                    value={formData.notifyDate}
                    max={formData.date}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.notifyDate ? styles.inputError : {})
                    }}
                  />
                  {errors.notifyDate && (
                    <div style={styles.errorText}>{errors.notifyDate}</div>
                  )}
                  <div style={styles.helpText}>
                    ※ 通知日を設定すると、その日にスタッフに通知が送信されます
                  </div>
                </div>

                <div style={styles.submitInfo}>
                  {assignmentType === "individual" ? (
                    <p>選択したスタッフのカレンダーにイベントが追加されます</p>
                  ) : (
                    <p>
                      <strong>{previewMembers.length}名</strong>のスタッフのカレンダーにイベントが追加されます
                    </p>
                  )}
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => setActiveSection("basic")}
                    style={styles.backButton}
                  >
                    戻る
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      ...styles.submitButton,
                      ...(loading ? styles.disabledButton : {}),
                      ...(success ? styles.successButton : {})
                    }}
                  >
                    {loading ? "保存中..." : success ? "✓ 保存しました" : "イベントを登録"}
                  </button>
                </div>
                
                {errors.submit && (
                  <div style={styles.submitError}>{errors.submit}</div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// スタイル定義
const styles = {
  container: {
    fontFamily: "'Noto Sans JP', sans-serif",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  content: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "2rem 1rem",
  },
  titleSection: {
    marginBottom: "2rem",
  },
  mainTitle: {
    fontSize: "1.75rem",
    fontWeight: "700",
    color: "#1a73e8",
    marginBottom: "0.5rem",
  },
  subtitle: {
    fontSize: "1rem",
    color: "#5f6368",
    marginBottom: "1rem",
  },
  listLink: {
    display: "inline-block",
    color: "#1a73e8",
    textDecoration: "none",
    fontWeight: "500",
    fontSize: "0.9rem",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  },
  formNav: {
    display: "flex",
    borderBottom: "1px solid #e0e0e0",
  },
  navButton: {
    flex: 1,
    padding: "1rem",
    fontSize: "0.95rem",
    fontWeight: "500",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    borderBottom: "3px solid transparent",
    transition: "all 0.2s",
  },
  activeNavButton: {
    borderBottom: "3px solid #1a73e8",
    color: "#1a73e8",
  },
  form: {
    padding: "1.5rem",
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontWeight: "500",
    fontSize: "0.95rem",
    marginBottom: "0.5rem",
    color: "#202124",
  },
  required: {
    color: "#ea4335",
  },
  input: {
    padding: "0.75rem",
    fontSize: "1rem",
    border: "1px solid #dadce0",
    borderRadius: "4px",
    transition: "border 0.2s",
    outline: "none",
  },
  textarea: {
    minHeight: "100px",
    resize: "vertical",
  },
  inputError: {
    border: "1px solid #ea4335",
  },
  errorText: {
    color: "#ea4335",
    fontSize: "0.85rem",
    marginTop: "0.25rem",
  },
  helpText: {
    color: "#5f6368",
    fontSize: "0.85rem",
    marginTop: "0.25rem",
  },
  formActions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "1rem",
  },
  backButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#f1f3f4",
    color: "#3c4043",
    border: "none",
    borderRadius: "4px",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  nextButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginLeft: "auto",
  },
  submitButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginLeft: "auto",
  },
  disabledButton: {
    backgroundColor: "#dadce0",
    cursor: "not-allowed",
  },
  successButton: {
    backgroundColor: "#34a853",
  },
  submitError: {
    backgroundColor: "#fdeded",
    border: "1px solid #f9d7d7",
    color: "#ea4335",
    padding: "0.75rem",
    borderRadius: "4px",
    marginTop: "1rem",
    fontSize: "0.9rem",
  },
  toggleContainer: {
    display: "flex",
    border: "1px solid #dadce0",
    borderRadius: "4px",
    overflow: "hidden",
  },
  toggleButton: {
    flex: 1,
    padding: "0.75rem",
    fontSize: "0.95rem",
    border: "none",
    backgroundColor: "#f1f3f4",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  activeToggle: {
    backgroundColor: "#1a73e8",
    color: "white",
    fontWeight: "500",
  },
  previewBox: {
    marginTop: "0.75rem",
    padding: "0.75rem",
    backgroundColor: "#f1f9fe",
    borderRadius: "4px",
    border: "1px solid #d2e3fc",
  },
  previewTitle: {
    fontSize: "0.9rem",
    fontWeight: "500",
    color: "#1a73e8",
    marginTop: 0,
    marginBottom: "0.5rem",
  },
  previewMembers: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  memberChip: {
    display: "inline-block",
    padding: "0.4rem 0.75rem",
    backgroundColor: "white",
    border: "1px solid #d2e3fc",
    borderRadius: "16px",
    fontSize: "0.85rem",
  },
  emptyPreview: {
    marginTop: "0.75rem",
    padding: "0.75rem",
    backgroundColor: "#fef7e0",
    borderRadius: "4px",
    border: "1px solid #feefc3",
    color: "#b06000",
    fontSize: "0.9rem",
  },
  submitInfo: {
    backgroundColor: "#e6f4ea",
    border: "1px solid #ceead6",
    color: "#137333",
    padding: "0.75rem",
    borderRadius: "4px",
    fontSize: "0.9rem",
  }
};

export default AdminEvents;